import { Box, Button, Flex, Icon, useDisclosure } from '@chakra-ui/react';
import type { CardProps } from '@ui/components/Card';
import { Card } from '@ui/components/Card';
import { useTranslations } from '@ui/i18n';
import {
  getNftCollectionMintedLink,
  getNftCollectionName,
} from '@ui/queries/types';
import { ExpandMore } from '@veno-app/icons';
import type { NftCollection } from '@veno-app/sdk';
import type { PropsWithChildren } from 'react';
import React, { Children } from 'react';

import GardenNftCountdown from './GardenNftCountdown';

export type ExpandableCollectionProps = {
  collection: NftCollection;
  collectionNumber: number | undefined;
};

function ExpandableCollection({
  collection,
  children,
  collectionNumber,
  ...cardProps
}: PropsWithChildren<ExpandableCollectionProps> & CardProps) {
  const t = useTranslations();
  const { isOpen: isExpanded, onToggle: toggleExpanded } = useDisclosure();
  const childArray = Children.toArray(children);

  return (
    <Card
      m="0"
      py="16px"
      px="8px"
      position="relative"
      isActive={isExpanded}
      cursor="pointer"
      onClick={toggleExpanded}
      {...cardProps}
    >
      <Icon
        as={ExpandMore}
        boxSize="24px"
        position="absolute"
        top="16px"
        right="8px"
        color="text-link"
        transform={isExpanded ? 'rotate(180deg)' : undefined}
      />
      <Flex gap={4}>
        {collectionNumber !== undefined
          ? t('{collection} in wallet ({num})', {
              collection: getNftCollectionName(collection),
              num: collectionNumber,
            })
          : getNftCollectionName(collection)}
      </Flex>
      <Flex justify="center">
        <GardenNftCountdown collection={collection} />
      </Flex>
      {!collectionNumber && isExpanded && (
        <Flex justify="center" flexDirection="column">
          <Box
            mx="auto"
            mt="28px"
            maxW="300px"
            textAlign="center"
            textStyle="body"
          >
            {t("You don't have any {name} in your wallet", {
              name: getNftCollectionName(collection),
            })}
          </Box>
          <Button
            onClick={(e) => e.stopPropagation()}
            color="pink-500"
            as="a"
            href={getNftCollectionMintedLink(collection)}
            target="_blank"
            rel="noopener noreferrer"
            variant="link"
            mt={2}
          >
            {t('Buy on {platform}', {
              platform: 'Minted',
            })}
          </Button>
        </Flex>
      )}
      {isExpanded && !!collectionNumber && childArray[0]}
    </Card>
  );
}

export default ExpandableCollection;
